import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Timer,
  Play,
  Pause,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Settings2,
  Activity,
  Search,
  Zap,
} from "lucide-react";

const NEU = {
  bg: "#F8FAFC",
  bgCard: "#FFFFFF",
  bgSurface: "#F1F5F9",
  border: "#E2E8F0",
  text: "#0F172A",
  textMuted: "#64748B",
  accent: "#0A3747",
  accentLight: "#E0E621",
};

const HEALTH_CONFIG = {
  ok:      { label: "Saludable", color: "#059669", bg: "#ECFDF5", icon: CheckCircle2 },
  warning: { label: "Sin ejecutar", color: "#D97706", bg: "#FFFBEB", icon: AlertTriangle },
  error:   { label: "Con errores", color: "#DC2626", bg: "#FEF2F2", icon: XCircle },
  paused:  { label: "Pausado", color: "#6B7280", bg: "#F3F4F6", icon: Pause },
};

function timeAgo(iso) {
  if (!iso) return "Nunca";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Hace segundos";
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  return `Hace ${Math.floor(hrs / 24)}d`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-EC", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export default function SystemTriggers() {
  const [triggers, setTriggers] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failLoading, setFailLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingInterval, setEditingInterval] = useState(null);
  const [newInterval, setNewInterval] = useState("");
  const [saving, setSaving] = useState(null);
  const [runningTrigger, setRunningTrigger] = useState(null);
  const [fullSyncRunning, setFullSyncRunning] = useState(false);
  const [tab, setTab] = useState("triggers");

  const fetchTriggers = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/system/triggers");
      setTriggers(data.items || []);
    } catch (e) {
      toast.error("Error cargando triggers");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFailures = useCallback(async () => {
    setFailLoading(true);
    try {
      const { data } = await api.get("/v1/system/triggers/failures?hours=24");
      setFailures(data.items || []);
    } catch {
      toast.error("Error cargando fallos");
    } finally {
      setFailLoading(false);
    }
  }, []);

  useEffect(() => { fetchTriggers(); fetchFailures(); }, [fetchTriggers, fetchFailures]);

  const handleToggleStatus = async (trigger) => {
    const newStatus = trigger.status === "active" ? "paused" : "active";
    setSaving(trigger.id);
    try {
      await api.patch(`/v1/system/triggers/${trigger.id}`, { status: newStatus });
      toast.success(`Trigger ${newStatus === "active" ? "activado" : "pausado"}`);
      await fetchTriggers();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(null);
    }
  };

  const handleSaveInterval = async (trigger) => {
    const iv = parseInt(newInterval, 10);
    if (!iv || iv < 1) { toast.error("Intervalo debe ser >= 1 minuto"); return; }
    setSaving(trigger.id);
    try {
      await api.patch(`/v1/system/triggers/${trigger.id}`, { interval_minutes: iv });
      toast.success(`Intervalo actualizado a ${iv} min`);
      setEditingInterval(null);
      setNewInterval("");
      await fetchTriggers();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(null);
    }
  };

  // v98.4.0 · Ejecutar trigger manualmente
  const handleRunNow = async (trigger) => {
    setRunningTrigger(trigger.id);
    const prevCount = trigger.run_count;
    try {
      await api.post(`/v1/system/triggers/${trigger.id}/run`);
      toast.info(`"${trigger.name}" programado — esperando ejecución...`);
      // Poll hasta que run_count cambie o timeout (30s)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data } = await api.get("/v1/system/triggers");
          const updated = (data.items || []).find(t => t.id === trigger.id);
          if (updated && updated.run_count > prevCount) {
            clearInterval(poll);
            setRunningTrigger(null);
            setTriggers(data.items || []);
            if (updated.health === "error") {
              toast.error(`"${trigger.name}" ejecutado con errores`);
            } else {
              toast.success(`"${trigger.name}" ejecutado correctamente`);
            }
          } else if (attempts >= 15) {
            clearInterval(poll);
            setRunningTrigger(null);
            setTriggers(data.items || []);
            toast.warning(`"${trigger.name}" aún en cola — verifique en unos segundos`);
          }
        } catch {
          if (attempts >= 15) { clearInterval(poll); setRunningTrigger(null); }
        }
      }, 2000);
    } catch (e) {
      setRunningTrigger(null);
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };


  // v98.7.0 · Sync completo para ot_materialize
  const handleFullSync = async () => {
    setFullSyncRunning(true);
    try {
      // v100.7.0 · Corre en segundo plano; se consulta su avance cada 5 s (máx. 30 min)
      const { data } = await api.post("/v1/system/triggers/ot-materialize/full-sync");
      toast.info(data.status === "already_running" ? "Ya hay un sync completo en curso…" : "Sync completo iniciado en segundo plano…");
      for (let i = 0; i < 360; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: st } = await api.get("/v1/system/triggers/ot-materialize/full-sync");
          if (st.status === "ok") {
            toast.success(`Sync completo: ${st.result?.rows_upserted ?? 0} filas procesadas (${st.result?.total_master_ot ?? "?"} en master_ot)`);
            break;
          }
          if (st.status === "error") { toast.error(`Sync completo falló: ${st.error || "sin detalle"}`); break; }
          if (st.status === "idle") { toast.warning("El sync completo se interrumpió (reinicio del servidor)."); break; }
        } catch { /* reintentar */ }
      }
      await fetchTriggers();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setFullSyncRunning(false);
    }
  };

  const TABS = [
    { key: "triggers", label: "Procesos", icon: Settings2 },
    { key: "failures", label: "Fallos (24h)", icon: AlertTriangle },
  ];

  const healthCounts = {
    ok: triggers.filter(t => t.health === "ok").length,
    error: triggers.filter(t => t.health === "error").length,
    paused: triggers.filter(t => t.health === "paused").length,
    warning: triggers.filter(t => t.health === "warning").length,
  };

  return (
    <Layout title="Gestor de Triggers" icon={<Timer size={20} />}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="system-triggers-page">

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(HEALTH_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <div
                key={key}
                className="rounded-xl p-4 flex items-center gap-3 border"
                style={{ background: cfg.bg, borderColor: `${cfg.color}30` }}
                data-testid={`trigger-kpi-${key}`}
              >
                <Icon size={20} style={{ color: cfg.color }} />
                <div>
                  <div className="text-2xl font-bold" style={{ color: cfg.color }}>{healthCounts[key]}</div>
                  <div className="text-[11px] font-medium" style={{ color: cfg.color }}>{cfg.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: NEU.bgSurface }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all flex-1 justify-center"
                style={{
                  background: isActive ? NEU.accent : "transparent",
                  color: isActive ? NEU.accentLight : NEU.textMuted,
                }}
                data-testid={`triggers-tab-${t.key}`}
              >
                <Icon size={14} />
                {t.label}
                {t.key === "failures" && failures.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full font-bold"
                    style={{ background: "#DC2626", color: "#fff" }}>
                    {failures.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab: Triggers */}
        {tab === "triggers" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-semibold" style={{ color: NEU.textMuted }}>
                {triggers.length} procesos registrados
              </span>
              <button
                onClick={() => { setLoading(true); fetchTriggers(); }}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
                style={{ background: NEU.accent, color: NEU.accentLight }}
                data-testid="triggers-refresh"
              >
                <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refrescar
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin" style={{ color: NEU.textMuted }} />
              </div>
            ) : (
              <div className="space-y-2">
                {triggers.map(tr => {
                  const hc = HEALTH_CONFIG[tr.health] || HEALTH_CONFIG.warning;
                  const HIcon = hc.icon;
                  const isExpanded = expandedRow === tr.id;
                  const isEditing = editingInterval === tr.id;

                  return (
                    <div
                      key={tr.id}
                      className="rounded-xl border overflow-hidden transition-all"
                      style={{ background: NEU.bgCard, borderColor: NEU.border }}
                      data-testid={`trigger-row-${tr.code}`}
                    >
                      {/* Header */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-all"
                        onClick={() => setExpandedRow(isExpanded ? null : tr.id)}
                      >
                        {isExpanded ? <ChevronDown size={14} style={{ color: NEU.textMuted }} /> : <ChevronRight size={14} style={{ color: NEU.textMuted }} />}

                        {/* Health badge */}
                        <div className="px-2 py-1 rounded-lg flex items-center gap-1.5" style={{ background: hc.bg }}>
                          <HIcon size={12} style={{ color: hc.color }} />
                          <span className="text-[10px] font-bold" style={{ color: hc.color }}>{hc.label}</span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold truncate" style={{ color: NEU.text }}>{tr.name}</div>
                          <div className="text-[10px] font-mono" style={{ color: NEU.textMuted }}>{tr.code}</div>
                        </div>

                        {/* Interval */}
                        <div className="text-center px-3">
                          <div className="text-xs font-bold" style={{ color: NEU.accent }}>{tr.interval_minutes}m</div>
                          <div className="text-[10px]" style={{ color: NEU.textMuted }}>intervalo</div>
                        </div>

                        {/* Last run */}
                        <div className="text-center px-3 hidden md:block">
                          <div className="text-xs font-semibold" style={{ color: NEU.text }}>{timeAgo(tr.last_run)}</div>
                          <div className="text-[10px]" style={{ color: NEU.textMuted }}>ejecutado</div>
                        </div>

                        {/* Stats */}
                        <div className="text-center px-3 hidden lg:block">
                          <div className="text-xs">
                            <span className="font-bold" style={{ color: "#059669" }}>{tr.run_count}</span>
                            {tr.fail_count > 0 && (
                              <span className="ml-1 font-bold" style={{ color: "#DC2626" }}>/ {tr.fail_count} err</span>
                            )}
                          </div>
                          <div className="text-[10px]" style={{ color: NEU.textMuted }}>ejecuciones</div>
                        </div>

                        {/* Toggle */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleStatus(tr); }}
                          disabled={saving === tr.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80"
                          style={{
                            background: tr.status === "active" ? "#ECFDF5" : "#F3F4F6",
                            color: tr.status === "active" ? "#059669" : "#6B7280",
                            border: `1px solid ${tr.status === "active" ? "#A7F3D0" : "#D1D5DB"}`,
                          }}
                          data-testid={`trigger-toggle-${tr.code}`}
                        >
                          {saving === tr.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : tr.status === "active" ? (
                            <Play size={12} />
                          ) : (
                            <Pause size={12} />
                          )}
                          {tr.status === "active" ? "Activo" : "Pausado"}
                        </button>

                        {/* v98.4.0 · Ejecutar ahora */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRunNow(tr); }}
                          disabled={runningTrigger === tr.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80"
                          style={{
                            background: runningTrigger === tr.id ? "#DBEAFE" : "#EFF6FF",
                            color: runningTrigger === tr.id ? "#1D4ED8" : "#1E40AF",
                            border: `1px solid ${runningTrigger === tr.id ? "#93C5FD" : "#BFDBFE"}`,
                          }}
                          data-testid={`trigger-run-${tr.code}`}
                        >
                          {runningTrigger === tr.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Play size={12} />
                          )}
                          {runningTrigger === tr.id ? "Ejecutando..." : "Ejecutar"}
                        </button>

                        {/* v98.7.0 · Botón Sync Completo solo para ot_materialize */}
                        {tr.code === "ot_materialize" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleFullSync(); }}
                            disabled={fullSyncRunning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80"
                            style={{
                              background: fullSyncRunning ? "#FEF3C7" : "#FFFBEB",
                              color: fullSyncRunning ? "#B45309" : "#92400E",
                              border: `1px solid ${fullSyncRunning ? "#FCD34D" : "#FDE68A"}`,
                            }}
                            data-testid="trigger-full-sync"
                          >
                            {fullSyncRunning ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Zap size={12} />
                            )}
                            {fullSyncRunning ? "Sync completo..." : "Sync Completo"}
                          </button>
                        )}
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-5 pb-4 pt-1 space-y-3 border-t" style={{ borderColor: NEU.border, background: NEU.bgSurface }}>
                          {tr.description && (
                            <div className="text-xs" style={{ color: NEU.textMuted }}>{tr.description}</div>
                          )}

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: NEU.textMuted }}>Intervalo</div>
                              {isEditing ? (
                                <div className="flex gap-1.5 mt-1">
                                  <input
                                    type="number"
                                    min="1"
                                    value={newInterval}
                                    onChange={(e) => setNewInterval(e.target.value)}
                                    className="w-20 px-2 py-1 text-xs rounded border"
                                    style={{ borderColor: NEU.border }}
                                    autoFocus
                                    data-testid={`trigger-interval-input-${tr.code}`}
                                  />
                                  <button
                                    onClick={() => handleSaveInterval(tr)}
                                    disabled={saving === tr.id}
                                    className="px-2 py-1 text-[10px] font-bold rounded"
                                    style={{ background: NEU.accent, color: NEU.accentLight }}
                                    data-testid={`trigger-interval-save-${tr.code}`}
                                  >
                                    OK
                                  </button>
                                  <button
                                    onClick={() => { setEditingInterval(null); setNewInterval(""); }}
                                    className="px-2 py-1 text-[10px] rounded border"
                                    style={{ borderColor: NEU.border }}
                                  >
                                    X
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm font-bold" style={{ color: NEU.accent }}>{tr.interval_minutes} min</span>
                                  <button
                                    onClick={() => { setEditingInterval(tr.id); setNewInterval(String(tr.interval_minutes)); }}
                                    className="text-[10px] underline"
                                    style={{ color: NEU.textMuted }}
                                    data-testid={`trigger-interval-edit-${tr.code}`}
                                  >
                                    Editar
                                  </button>
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: NEU.textMuted }}>Ultima ejecucion</div>
                              <div className="text-xs font-semibold mt-1" style={{ color: NEU.text }}>{formatDateTime(tr.last_run)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: NEU.textMuted }}>Proxima ejecucion</div>
                              <div className="text-xs font-semibold mt-1" style={{ color: NEU.text }}>{formatDateTime(tr.next_run)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: NEU.textMuted }}>Ejecuciones / Fallos</div>
                              <div className="text-xs font-semibold mt-1">
                                <span style={{ color: "#059669" }}>{tr.run_count}</span>
                                <span style={{ color: NEU.textMuted }}> / </span>
                                <span style={{ color: tr.fail_count > 0 ? "#DC2626" : "#059669" }}>{tr.fail_count}</span>
                              </div>
                            </div>
                          </div>

                          {/* Last error */}
                          {tr.last_error && (
                            <div className="rounded-lg p-3 border" style={{ background: "#FEF2F2", borderColor: "#FECACA" }} data-testid={`trigger-error-${tr.code}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <XCircle size={12} style={{ color: "#DC2626" }} />
                                <span className="text-[10px] font-bold" style={{ color: "#991B1B" }}>
                                  Ultimo error ({formatDateTime(tr.last_error_at)})
                                </span>
                              </div>
                              <pre className="text-[11px] font-mono whitespace-pre-wrap break-all" style={{ color: "#B91C1C" }}>
                                {tr.last_error}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab: Failures Timeline */}
        {tab === "failures" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Activity size={14} style={{ color: "#DC2626" }} />
                <span className="text-xs font-semibold" style={{ color: NEU.textMuted }}>
                  {failures.length} ejecuciones fallidas en las ultimas 24h
                </span>
              </div>
              <button
                onClick={fetchFailures}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
                style={{ background: NEU.accent, color: NEU.accentLight }}
                data-testid="failures-refresh"
              >
                <RefreshCw size={12} className={failLoading ? "animate-spin" : ""} /> Refrescar
              </button>
            </div>

            {failLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin" style={{ color: NEU.textMuted }} />
              </div>
            ) : failures.length === 0 ? (
              <div className="text-center py-16 rounded-xl border" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }} data-testid="failures-empty">
                <CheckCircle2 size={32} className="mx-auto mb-2" style={{ color: "#059669" }} />
                <div className="text-sm font-bold" style={{ color: "#059669" }}>Sin fallos en las ultimas 24 horas</div>
                <div className="text-[11px] mt-1" style={{ color: "#6B7280" }}>Todos los procesos se ejecutaron correctamente.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {failures.map((f, i) => (
                  <div
                    key={f.id || i}
                    className="rounded-xl border p-4"
                    style={{ background: "#FEF2F2", borderColor: "#FECACA" }}
                    data-testid={`failure-row-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <XCircle size={16} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold" style={{ color: "#991B1B" }}>
                            {f.trigger_name || f.trigger_code}
                          </span>
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#B91C1C" }}>
                            {f.trigger_code}
                          </span>
                          <span className="text-[10px]" style={{ color: "#6B7280" }}>
                            {formatDateTime(f.started_at)}
                          </span>
                          {f.duration_ms != null && (
                            <span className="text-[10px]" style={{ color: "#6B7280" }}>
                              ({f.duration_ms}ms)
                            </span>
                          )}
                        </div>
                        {f.error_message && (
                          <pre className="text-[11px] font-mono mt-2 whitespace-pre-wrap break-all" style={{ color: "#B91C1C" }}>
                            {f.error_message}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
